import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import FileInput, {File} from "../../../../shared/components/FileInput";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {InputTextarea} from "primereact/inputtextarea";
import AttachmentDialogFields from "../../field/attachment/AttachmentDialogFields";
import {InputSwitch} from "primereact/inputswitch";
import {Dropdown} from "primereact/dropdown";
import {ColorPicker} from "primereact/colorpicker";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import validator from "validator";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	AttachmentFileData,
	BaseToolbox,
	FileAnnotation,
	FileAttachment,
	Functions,
	Icons,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxAttachmentAttachment
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import Exception from "../../../../shared/exception/Exception";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import Info from "../../../../shared/components/Info";

interface AttachmentDialogStatus extends PortalDialogState<ToolboxAttachmentAttachment> {
	attachmentFile: File;
}

export class AttachmentDialogDefinition extends PortalDialog<ToolboxAttachmentAttachment,
	PortalDialogProperties<ToolboxAttachmentAttachment>, AttachmentDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<AttachmentDialogFields, string>(),
			attachmentFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAttachment, Functions.getEditAttachmentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ATTACHMENT_ADD, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["attachment-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.attachment.page"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAttachmentAttachment | undefined = this.state.parameter?.data;
		let errors: Map<AttachmentDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let fileParameter: FileAttachment = parameter!.add!.file![0];

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AttachmentDialogFields.FILE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-file">
							<Translate {...this.props} data="attachment.properties.file" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<FileInput inputId="control-file" value={this.state.attachmentFile.value}
									   filename={this.state.attachmentFile.name} accept="*/*"
									   onChange={(files) => this.onChange(
										   AttachmentDialogFields.FILE, files[0]
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AttachmentDialogFields.TEXT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-popup">
							<Translate {...this.props} data="attachment.properties.popup" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputTextarea rows={2} id="control-popup"
										   value={FunctionHelper.applyValue(
											   fileParameter.annotation?.popupText, FileAnnotation.getPopupTextDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AttachmentDialogFields.TEXT, e.currentTarget.value
										   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AttachmentDialogFields.LOCKED)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-locked"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-locked"
										 checked={FunctionHelper.applyValue(
											 fileParameter.annotation?.lockedPosition,
											 FileAnnotation.getLockedPositionDefault()
										 )}
										 onChange={(e) => this.onChange(
											 AttachmentDialogFields.LOCKED, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-locked">
								<Translate {...this.props} data="attachment.properties.locked.label"
										   namespace="dialog"/>
								<Info {...this.props} data="attachment.properties.locked.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AttachmentDialogFields.ICON)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-icon">
							<Translate {...this.props} data="attachment.appearance.icon.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-icon"
									  value={FunctionHelper.applyValue(
										  fileParameter.annotation?.icon, FileAnnotation.getIconDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "attachment.appearance.icon.graph", {ns: "dialog"}
											  ),
											  value: Icons.Graph
										  },
										  {
											  label: Translate.value(
												  "attachment.appearance.icon.paperclip", {ns: "dialog"}
											  ),
											  value: Icons.Paperclip
										  },
										  {
											  label: Translate.value(
												  "attachment.appearance.icon.push_pin", {ns: "dialog"}
											  ),
											  value: Icons.PushPin
										  },
										  {
											  label: Translate.value(
												  "attachment.appearance.icon.tag", {ns: "dialog"}
											  ),
											  value: Icons.Tag
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  AttachmentDialogFields.ICON, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AttachmentDialogFields.COLOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-color">
							<Translate {...this.props} data="attachment.appearance.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={"p-inputgroup" + (
								errors.has(AttachmentDialogFields.COLOR) ? " p-invalid" : ""
							)}>
								<InputText className={errors.has(AttachmentDialogFields.COLOR) ? "p-invalid" : ""}
										   tooltip={errors.has(AttachmentDialogFields.COLOR) ? Translate.value(
											   errors.get(AttachmentDialogFields.COLOR)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(
											   fileParameter.annotation?.color, FileAnnotation.getColorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AttachmentDialogFields.COLOR, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-color" className="p-inputgroup-addon" format="hex"
											 defaultColor={
												 FileAnnotation.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 fileParameter.annotation?.color, FileAnnotation.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 AttachmentDialogFields.COLOR, "#" + e.value
											 )}
											 appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(AttachmentDialogFields.COLOR) ?
								Translate.value(
									errors.get(AttachmentDialogFields.COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AttachmentDialogFields.OPACITY)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-opacity">
							<Translate {...this.props} data="attachment.appearance.opacity" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-opacity" step={1} showButtons={true} suffix=" %"
										 useGrouping={false}
										 className={errors.has(AttachmentDialogFields.OPACITY) ? "p-invalid" : ""}
										 tooltip={errors.has(AttachmentDialogFields.OPACITY) ? Translate.value(
											 errors.get(AttachmentDialogFields.OPACITY)!, {ns: "message"}
										 ) : ""}
										 value={FunctionHelper.applyValue(
											 fileParameter.annotation?.opacity, FileAnnotation.getOpacityDefault()
										 )}
										 min={FileAnnotation.getOpacityMin()} max={FileAnnotation.getOpacityMax()}
										 onChange={(e) => this.onChange(
											 AttachmentDialogFields.OPACITY, e.value
										 )}/>
							{this.renderFieldError(errors.has(AttachmentDialogFields.OPACITY) ?
								Translate.value(
									errors.get(AttachmentDialogFields.OPACITY)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: AttachmentDialogFields, value: any): void {
		let parameter: ToolboxAttachmentAttachment = this.state.parameter!.data;
		let fileParameter: FileAttachment = parameter.add!.file![0];
		let errors: Map<AttachmentDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case AttachmentDialogFields.FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let file: File = value;
				fileParameter.fileName = file.name;
				fileParameter.mimeType = file.mimetype;
				fileParameter.data!.value = file.value;

				this.setState({
					attachmentFile: file,
					parameter: this.state.parameter
				});
				return;
			case AttachmentDialogFields.LOCKED:
				fileParameter.annotation!.lockedPosition = value;
				break;
			case AttachmentDialogFields.TEXT:
				fileParameter.annotation!.popupText = value;
				break;
			case AttachmentDialogFields.ICON:
				fileParameter.annotation!.icon = value;
				break;
			case AttachmentDialogFields.COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				fileParameter.annotation!.color = value;
				break;
			case AttachmentDialogFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = FileAnnotation.getOpacityDefault();
				}

				fileParameter.annotation!.opacity = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-attachment small"}/>
					<Translate {...this.props} data="attachment.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxAttachmentAttachment | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add?.file)) {
			return;
		}

		let fileParameter: FileAttachment = parameter!.add!.file![0];
		fileParameter.fileName = "";
		fileParameter.mimeType = "";
		fileParameter.data = new AttachmentFileData({});

		this.setState({
			attachmentFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone()
		});
	};

	protected async submit(): Promise<void> {
		let parameter: ToolboxAttachmentAttachment = this.state.parameter!.data;
		if (!FunctionHelper.exists(parameter.add!.file![0].data.value)) {
			this.setState({
				exception: new Exception(Translate.value("errors.files.undefined", {ns: "message"})),
				submitted: false
			});
			return;
		}

		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			attachment: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.attachment.add", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		this.resetMouseMode(documentManager.getSelection());
		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ATTACHMENT_ADD;
	}

	protected getDefaultParameter(): Parameter<ToolboxAttachmentAttachment> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			attachmentFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		})
	}
}

const AttachmentDialog = React.memo(AttachmentDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxAttachmentAttachment>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxAttachmentAttachment>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AttachmentDialog;